import { React, useCallback } from 'react';
import MaterialSymbol from '../../MaterialSymbol';
// import Gallery from '../../Gallery';

const TableInputStyles = `
    .font-barlow {
        font-family: 'Barlow', sans-serif;
    }

    .bg-grey {
        background-color: #D9D9D9
    }

    .color-dark-gray {
        color: #535353;
    }

    .column-input:focus {
        outline: none !important;
    }

    .mw-150 {
        max-width: 150px;
    }

    .min-w-150 {
        min-width: 150px;
    }

    .mh-134 {
        max-height: 134px;
    }

    .table-custom tbody td:first-child,
    .table-custom tr:first-child th {
        background-color: #ececec;
    }
`;

/**
 * Componente responsável por exibir e gerenciar entradas em formato de tabela.
 * @param {Object} props - Propriedades do componente.
 * @param {Function} props.onAnswerChange - Função chamada quando uma célula da tabela é atualizada.
 * @param {string} props.applicationAnswerId - ID da resposta da aplicação.
 * @param {Object} props.group - Objeto representando o grupo de perguntas da tabela.
 * @param {Array} props.answers - Lista de respostas associadas à tabela.
 * @param {number} props.answersPage - Página atual das respostas.
 * @param {boolean} props.disabled - Define se a interação com o componente está desabilitada.
 */
function TableInput(props) {
    const { onAnswerChange, applicationAnswerId, group, answers, answersPage, disabled, isProtocol, pageIndex, groupIndex, answer } = props;

    /**
     * Atualiza a resposta da tabela.
     * @param {Object} newAnswer - Novo objeto de resposta.
     * @param {sring} itemId - ID do item dentro da tabela.
     */
    const updateAnswer = useCallback(
        (newAnswer, itemId) => {
            onAnswerChange(group.id, itemId, 'TABLE', newAnswer);
        },
        [onAnswerChange, group.id]
    );

    /**
     * Gerencia a atualização de uma célula da tabela.
     * @param {string} itemId - ID do item da tabela.
     * @param {number} columnId - ID da coluna.
     * @param {boolean} updatedText - Novo valor da célula.
     */
    const handleTableUpdate = (itemId, columnId, updatedText) => {
        let newAnswer = { ...answers.find((a) => a.item === itemId).answer };
        if (group.type === 'RADIO_TABLE') newAnswer = { [`${columnId}`]: '' };
        if (updatedText === '' || !updatedText) {
            delete newAnswer[columnId];
        } else {
            if (updatedText === true) newAnswer[columnId] = '';
            else newAnswer[columnId] = updatedText;
        }
        updateAnswer({ ...newAnswer, group: group.id }, itemId);
    };

    return (
        <div className={`rounded-4 ${!isProtocol ? 'p-3' : ''} ${answersPage ? 'bg-light-gray' : 'shadow bg-white'}`}>
            {!isProtocol && (
                <div className="row m-0">
                    <div className="col d-flex flex-wrap justify-content-start align-items-center p-0 mb-3">
                        {answersPage && (
                            <>
                                <span className="color-gray fw-medium">{`Aplicações`}</span>
                                <MaterialSymbol icon="chevron_right" size={18} fill={1} grade={200} weight={400} className="mx-1" />
                                <span className="color-gray fw-medium">{`Página ${pageIndex + 1}`}</span>
                                <MaterialSymbol icon="chevron_right" size={18} fill={1} grade={200} weight={400} className="mx-1" />
                                <span className="color-gray fw-medium">{`Grupo ${groupIndex + 1}`}</span>
                                <MaterialSymbol icon="chevron_right" size={18} fill={1} grade={200} weight={400} className="mx-1" />
                            </>
                        )}
                        {(() => {
                            switch (group.type) {
                                case 'TEXTBOX_TABLE':
                                    return <h2 className="d-block color-dark-gray fw-medium fs-5 mb-0">Tabela de texto</h2>;

                                case 'RADIO_TABLE':
                                    return <h2 className="d-block color-dark-gray fw-medium fs-5 mb-0">Tabela de escolha simples</h2>;

                                case 'CHECKBOX_TABLE':
                                    return <h2 className="d-block color-dark-gray fw-medium fs-5 mb-0">Tabela de múltipla escolha</h2>;

                                default:
                                    return;
                            }
                        })()}
                    </div>
                </div>
            )}

            <div className="table-grid overflow-auto w-auto">
                <table className="table-custom table table-bordered border-black m-0">
                    <thead>
                        <tr>
                            <th scope="col" className="min-w-150 mw-150 mh-134"></th>
                            {group.tableColumns?.map((column, columnIndex) => {
                                return (
                                    <th
                                        key={'column-' + columnIndex}
                                        scope="col"
                                        className="align-middle overflow-auto min-w-150 mw-150 mh-134"
                                    >
                                        <div className="overflow-auto text-break text-center font-barlow fw-semibold fs-6 color-dark-gray mh-134">
                                            {column.text}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {group.items?.map((item, itemIndex) => {
                            const rowAnswerData = answers?.find((a) => a.item === item.id)?.answer || {};

                            return (
                                <tr key={'item-line-' + itemIndex}>
                                    <th scope="row" className="min-w-150 mw-150 mh-134">
                                        <div className="overflow-auto text-break font-barlow fw-semibold fs-6 color-dark-gray mh-134">
                                            {item.text}
                                        </div>
                                    </th>

                                    {!answersPage &&
                                        group.tableColumns?.map((column, columnIndex) => {
                                            const cellValue = rowAnswerData ? rowAnswerData[column.id] : null;
                                            const isChecked = rowAnswerData ? Object.hasOwn(rowAnswerData, column.id) : false;

                                            const uniqueInputId = `input-${item.id}-${column.id}`;

                                            return (
                                                <td
                                                    key={'cell-' + item.id + '-' + column.id}
                                                    className="overflow-auto min-w-150 mw-150 mh-134"
                                                >
                                                    {group.type === 'TEXTBOX_TABLE' && (
                                                        <textarea
                                                            type="text"
                                                            className="column-input border border-0 font-barlow fw-medium fs-6 color-dark-gray w-100"
                                                            id={uniqueInputId}
                                                            value={cellValue || ''}
                                                            onChange={(e) => handleTableUpdate(item.id, column.id, e.target.value, true)}
                                                            disabled={disabled}
                                                        ></textarea>
                                                    )}
                                                    {group.type === 'RADIO_TABLE' && (
                                                        <input
                                                            className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                            type="radio"
                                                            name={'column-option-for-item-' + item.id}
                                                            id={uniqueInputId}
                                                            checked={isChecked}
                                                            onChange={(e) => handleTableUpdate(item.id, column.id, e.target.checked)}
                                                            disabled={disabled}
                                                        ></input>
                                                    )}
                                                    {group.type === 'CHECKBOX_TABLE' && (
                                                        <input
                                                            className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                            type="checkbox"
                                                            name={'column-option-for-item-' + item.id}
                                                            id={uniqueInputId}
                                                            checked={isChecked}
                                                            onChange={(e) => handleTableUpdate(item.id, column.id, e.target.checked)}
                                                            disabled={disabled}
                                                        ></input>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    {answersPage &&
                                        !isProtocol &&
                                        Object.entries(item.tableAnswers).map(([applicationAnsId, answerGroup]) => {
                                            return (
                                                applicationAnsId === applicationAnswerId &&
                                                Object.entries(answerGroup).map(([answerGroupId, groupAnswers]) => {
                                                    return group.tableColumns?.map((column, columnIndex) => {
                                                        const answerEntry = Object.entries(groupAnswers).find(
                                                            ([key]) => String(key) === String(column.id)
                                                        );
                                                        const value = answerEntry ? answerEntry[1] : null;

                                                        const uniqueInputId = `input-${item.id}-${column.id}-answer-${applicationAnsId}`;
                                                        return (
                                                            <td
                                                                key={'ans-' + answerGroupId + '-' + item.id + '-' + column.id}
                                                                className="overflow-auto min-w-150 mw-150 mh-134"
                                                            >
                                                                {group.type === 'TEXTBOX_TABLE' && (
                                                                    <textarea
                                                                        type="text"
                                                                        className="column-input border border-0 font-barlow fw-medium fs-6 color-dark-gray w-100"
                                                                        id={uniqueInputId}
                                                                        value={value ? value : ''}
                                                                        disabled={disabled}
                                                                    ></textarea>
                                                                )}
                                                                {group.type === 'RADIO_TABLE' && (
                                                                    <input
                                                                        className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                                        type="radio"
                                                                        name={`column-option-for-item-${item.id}-column-${column.id}-application-${applicationAnsId}`}
                                                                        id={uniqueInputId}
                                                                        checked={value === null ? false : true}
                                                                        disabled={disabled}
                                                                    ></input>
                                                                )}
                                                                {group.type === 'CHECKBOX_TABLE' && (
                                                                    <input
                                                                        className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                                        type="checkbox"
                                                                        name={'column-option-for-item-' + item.id}
                                                                        id={uniqueInputId}
                                                                        checked={value === null ? false : true}
                                                                        disabled={disabled}
                                                                    ></input>
                                                                )}
                                                            </td>
                                                        );
                                                    });
                                                })
                                            );
                                        })}
                                    {answersPage &&
                                        isProtocol &&
                                        group.tableColumns?.map((tableColumn) => {
                                            const tableAnswer = item.tableAnswers.find(
                                                (tableAnswer) =>
                                                    tableAnswer.group.applicationAnswerId === applicationAnswerId &&
                                                    tableAnswer.columnId === tableColumn.id
                                            );

                                            const uniqueInputId = `input-${item.id}-${tableColumn.id}-protocol-${applicationAnswerId}`;
                                            return (
                                                <td
                                                    key={'prot-' + item.id + '-' + tableColumn.id}
                                                    className="overflow-auto min-w-150 mw-150 mh-134"
                                                >
                                                    {group.type === 'TEXTBOX_TABLE' && (
                                                        <textarea
                                                            type="text"
                                                            className="column-input border border-0 font-barlow fw-medium fs-6 color-dark-gray w-100"
                                                            id={uniqueInputId}
                                                            value={tableAnswer ? tableAnswer.text : ''}
                                                            disabled={disabled}
                                                        ></textarea>
                                                    )}
                                                    {group.type === 'RADIO_TABLE' && (
                                                        <input
                                                            className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                            type="radio"
                                                            name={`column-option-for-item-${item.id}-column-${tableColumn.id}-application-${applicationAnswerId}`}
                                                            id={uniqueInputId}
                                                            checked={tableAnswer ? false : true}
                                                            disabled={disabled}
                                                        ></input>
                                                    )}
                                                    {group.type === 'CHECKBOX_TABLE' && (
                                                        <input
                                                            className={`column-input font-barlow fw-medium fs-6 color-dark-gray w-100`}
                                                            type="checkbox"
                                                            name={`column-option-for-item-${item.id}-column-${tableColumn.id}-application-${applicationAnswerId}`}
                                                            id={uniqueInputId}
                                                            checked={tableAnswer ? false : true}
                                                            disabled={disabled}
                                                        ></input>
                                                    )}
                                                </td>
                                            );
                                        })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {answersPage && !isProtocol && (
                <div className="d-flex mt-2 pt-2" style={{ borderTop: '1px solid #787878' }}>
                    <MaterialSymbol icon="person_edit" size={24} color="#535353" fill={0} />
                    <span className="fw-medium fs-6 color-dark-gray ps-2 m-0">
                        {` ${answer.user.username} • ${new Date(answer.date).toLocaleDateString()}`}
                    </span>
                </div>
            )}

            <style>{TableInputStyles}</style>
        </div>
    );
}

export default TableInput;
